/**
 * The bay key of a lesson save, by the scope of the save.
 *
 * - A create and a single occurrence edit always send `scheduleResourceId`,
 *   as the form holds it.
 * - A series edit ("this and following") sends the key when the user picked
 *   a value in the Resource field, also the bay that the field already showed
 *   (Adrian, 2026-10-07: an admin can then put a whole series in one bay), or
 *   when the bay changed in another way (a new booking option). "Auto" and
 *   "No resource" count too. The service then puts every occurrence in that
 *   bay, or answers 409 with the first taken time.
 * - A series edit with no pick and the same bay leaves the key out. The
 *   occurrences of a series can sit in different bays, because "Auto" can
 *   spread them. The form shows the bay of the opened occurrence only, so that
 *   bay must not go to the other occurrences. `null` is not a safe default
 *   either: for a custom lesson, `null` clears the bay. With no key, the
 *   service keeps the bay of each occurrence while it is free. An option
 *   lesson can then take another free bay of its option, and a custom lesson
 *   gets a 409.
 */
export type SaveScope = "create" | "single" | "series";

export function bayKeyForScope<P extends object>(
  payload: P,
  scope: SaveScope,
  heldBayId: number | null | undefined,
  picked: boolean = false,
): P {
  if (scope !== "series" || !("scheduleResourceId" in payload)) {
    return payload;
  }

  const { scheduleResourceId, ...rest } = payload as P & {
    scheduleResourceId?: number | null;
  };
  if (picked || (scheduleResourceId ?? null) !== (heldBayId ?? null)) {
    return payload;
  }

  return rest as P;
}

/**
 * The payload of a "this and following" lesson edit. The coach calendar and
 * the facility calendar both send it, so the bay rule lives in one place.
 * `form` is the submitted form: `scheduleResourcePicked` is true after the
 * user picked a value in the Resource field.
 */
export function followingEditPayload<P extends object>(
  payload: P,
  form: { scheduleResourcePicked?: boolean },
  heldBayId: number | null | undefined,
): P & { applyToRecurrence: "following" } {
  return bayKeyForScope(
    { ...payload, applyToRecurrence: "following" as const },
    "series",
    heldBayId,
    form.scheduleResourcePicked === true,
  );
}
